import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Modal } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationAuthMethodEnum } from 'src/api/main'
import {
  accountLinkedAuthMethodsRetrieveQueryKey,
  accountSocialAccountsDestroyMutation,
  authorizationSocialAppleConnectUrlCreateMutation,
  authorizationSocialGoogleClientConnectUrlCreateMutation
} from 'src/api/main/@tanstack/react-query.gen'
import { paths } from 'src/router'
import { getSocialAuthRedirectOrigin, useShowApiErrors } from 'src/utils'

import type { LinkableAccountProvider, SocialAccountProvider } from './types'

export const useSocialAccountIntegration = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const [linkingProvider, setLinkingProvider] =
    useState<LinkableAccountProvider | null>(null)

  const unlinkMutation = useMutation({
    ...accountSocialAccountsDestroyMutation(),
    onSuccess: async () => {
      message.success(t('account.messages.providerUnlinked'))
      await queryClient.invalidateQueries({
        queryKey: accountLinkedAuthMethodsRetrieveQueryKey()
      })
    },
    onError: (error) => showApiErrors(error)
  })

  const googleConnectUrlMutation = useMutation({
    ...authorizationSocialGoogleClientConnectUrlCreateMutation(),
    onSuccess: (data) => {
      const authUrl = (data as { auth_url?: string })?.auth_url
      if (authUrl) {
        window.location.assign(authUrl)
        return
      }

      setLinkingProvider(null)
      message.error(t('errors.somethingWentWrong'))
    },
    onError: (error) => {
      showApiErrors(error)
      setLinkingProvider(null)
    }
  })

  const appleConnectUrlMutation = useMutation({
    ...authorizationSocialAppleConnectUrlCreateMutation(),
    onSuccess: (data) => {
      const authUrl = (data as { auth_url?: string })?.auth_url
      if (authUrl) {
        window.location.assign(authUrl)
        return
      }

      setLinkingProvider(null)
      message.error(t('errors.somethingWentWrong'))
    },
    onError: (error) => {
      showApiErrors(error)
      setLinkingProvider(null)
    }
  })

  return {
    handleLinkApple: () => {
      setLinkingProvider('apple')
      const redirectOrigin = getSocialAuthRedirectOrigin()
      appleConnectUrlMutation.mutate({
        body: {
          redirect_uri: `${redirectOrigin}${paths.auth.appleClientConnectCallback}`,
          return_origin: window.location.href
        }
      })
    },
    handleLinkGoogle: () => {
      setLinkingProvider('google')
      const redirectOrigin = getSocialAuthRedirectOrigin()
      googleConnectUrlMutation.mutate({
        body: {
          redirect_uri: `${redirectOrigin}${paths.auth.googleClientConnectCallback}`,
          return_origin: window.location.href
        }
      })
    },
    handleUnlinkProvider: (provider: SocialAccountProvider) => {
      const unlinkTitle =
        provider === OrganizationAuthMethodEnum.APPLE
          ? t('profileAuthMethods.unlinkConfirmTitleApple')
          : t('profileAuthMethods.unlinkConfirmTitle')
      const unlinkContent =
        provider === OrganizationAuthMethodEnum.APPLE
          ? t('profileAuthMethods.unlinkConfirmContentApple')
          : t('profileAuthMethods.unlinkConfirmContent')

      Modal.confirm({
        cancelText: t('cancel'),
        content: unlinkContent,
        okButtonProps: { danger: true },
        okText: t('profileAuthMethods.unlink'),
        onOk: () => unlinkMutation.mutateAsync({ path: { provider } }),
        title: unlinkTitle
      })
    },
    isLinkingProvider: (provider: LinkableAccountProvider) =>
      linkingProvider === provider,
    isUnlinkPending: (provider: SocialAccountProvider) =>
      unlinkMutation.isPending &&
      unlinkMutation.variables?.path?.provider === provider
  }
}
